import Template from "../../components/Template";
import React, { useState, useEffect, useRef, useMemo, useCallback, memo } from "react";
import api from "../../services/api";
import config from "../../config";
import { formatDateWithoutHour } from "../../utils/date";
import * as XLSX from "xlsx";
import AlertDialog from "../../components/AlertDialog";
import usePermission from "../../hooks/usePermission";
import useListFilterState from "../../hooks/useListFilterState";

import useRequisicaoMaisRecente from '../../hooks/useRequisicaoMaisRecente';
// Status em que o boleto não está mais correndo: não faz sentido calcular
// "Vencido"/"Vence Hoje" em cima deles. ("closed" = baixado na Qive)
const STATUS_FINALIZADOS = ['paid', 'closed', 'canceled', 'cancelled'];

// Componente TableRow memoizado para melhor performance
const TableRow = memo(({ boleto, columns }) => {
    return (
        <tr key={boleto.id} className="hover:bg-gray-50">
            {columns.filter((col) => col.visible).map((col) => (
                <td key={`${boleto.id}-${col.id}`} className="px-4 py-2 border border-slate-200 text-slate-700 text-base">
                    {col.formatter(boleto[col.id], boleto)}
                </td>
            ))}
        </tr>
    );
}, (prevProps, nextProps) => {
    return prevProps.boleto.id === nextProps.boleto.id && 
           prevProps.columns === nextProps.columns;
});

const Boletos = () => {
    // Permissões
    const podeVisualizar = usePermission('VISUALIZAR_BOLETO');
    const podeAtualizar = usePermission('ATUALIZAR_BOLETO');
    const podeSincronizar = usePermission('SINCRONIZAR_BOLETO');
    const podeExportar = usePermission('EXPORTAR_BOLETO');

    const [boletos, setBoletos] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loading, setLoading] = useState(false);
    const renderStartTimeRef = useRef(null);
    const preRenderTimeRef = useRef(null);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [message, setMessage] = useState("");
    const [estatisticas, setEstatisticas] = useState(null);
    const [sincronizando, setSincronizando] = useState(false);
    
    // Modal de detalhes
    const [selectedBoleto, setSelectedBoleto] = useState(null);
    const [showDetalheModal, setShowDetalheModal] = useState(false);
    
    // Modal de PDF
    const [showPdfModal, setShowPdfModal] = useState(false);
    const [selectedPdfUrl, setSelectedPdfUrl] = useState("");
    
    // Modal de atualização de status
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [statusForm, setStatusForm] = useState({
        status: '',
        dataPagamento: '',
        observacao: ''
    });
    
    // Modal de vinculação de nota fiscal
    const [showVincularModal, setShowVincularModal] = useState(false);
    const [notasFiscaisDisponiveis, setNotasFiscaisDisponiveis] = useState([]);
    const [buscandoNotas, setBuscandoNotas] = useState(false);
    const [searchNotaFiscal, setSearchNotaFiscal] = useState('');
    const [tipoNotaVinculo, setTipoNotaVinculo] = useState('todos'); // 'material', 'servico', 'todos'
    const [notaSelecionadaVinculo, setNotaSelecionadaVinculo] = useState(null);

    // Modal de anexos/documentos
    const [showAnexosModal, setShowAnexosModal] = useState(false);
    const [anexosBoleto, setAnexosBoleto] = useState([]);
    const [tiposDocumentos, setTiposDocumentos] = useState([]);
    const [anexoTipoId, setAnexoTipoId] = useState('');
    const [anexoFile, setAnexoFile] = useState(null);
    const [loadingAnexos, setLoadingAnexos] = useState(false);
    const [enviandoAnexo, setEnviandoAnexo] = useState(false);

    // Filtros
    const { filters, setFilters, pageSize, setPageSize, clearPersistedState } = useListFilterState('boletos', {
        status: "",
        dataVencimentoInicial: "",
        dataVencimentoFinal: "",
        beneficiario: "",
        vinculadoNfe: "",
    });
    
    // Filtros efetivamente aplicados (botao "Filtrar", card ou "Limpar").
    const [appliedFilters, setAppliedFilters] = useState({});

    // Card ativo (filtro por card)
    const [activeCard, setActiveCard] = useState(null);

    const [menuOpen, setMenuOpen] = useState(null);
    const buttonRef = useRef(null);
    const buttonFiltrarRef = useRef(null);

    // Formatação de moeda
    const formatCurrency = (value) => {
        if (!value) return "R$ 0,00";
        const num = parseFloat(value);
        return num.toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL',
        });
    };

    // Traduz status do banco para português (considerando data de vencimento)
    const getStatusLabel = (status, dataVencimento = null) => {
        const statusLabels = {
            open: 'Em Aberto',
            paid: 'Pago',
            overdue: 'Vencido',
            closed: 'Baixado',
            canceled: 'Cancelado',
            // grafias legadas
            pending: 'Em Aberto',
            cancelled: 'Cancelado',
        };

        // Verificar se vence hoje (independente do status no banco)
        let statusFinal = status;
        if (!STATUS_FINALIZADOS.includes(status) && dataVencimento) {
            const hoje = new Date();
            hoje.setHours(0, 0, 0, 0);
            // Parse como data local (evita problema de timezone UTC vs BR)
            const partes = String(dataVencimento).split('T')[0].split('-');
            const vencimento = new Date(parseInt(partes[0]), parseInt(partes[1]) - 1, parseInt(partes[2]));

            if (vencimento.getTime() === hoje.getTime()) {
                return 'Vence Hoje';
            }
            if ((status === 'open' || status === 'pending') && vencimento.getTime() < hoje.getTime()) {
                statusFinal = 'overdue';
            }
        }

        return statusLabels[statusFinal] || statusFinal || 'Desconhecido';
    };

    // Status com cores (considera data de vencimento para mostrar "Vencido")
    const getStatusBadge = (status, dataVencimento = null) => {
        const statusConfig = {
            open: { label: 'Em Aberto', bg: 'bg-blue-100', text: 'text-blue-800' },
            paid: { label: 'Pago', bg: 'bg-green-100', text: 'text-green-800' },
            overdue: { label: 'Vencido', bg: 'bg-red-100', text: 'text-red-800' },
            closed: { label: 'Baixado', bg: 'bg-slate-100', text: 'text-slate-800' },
            canceled: { label: 'Cancelado', bg: 'bg-gray-100', text: 'text-gray-800' },
            // grafias legadas
            pending: { label: 'Em Aberto', bg: 'bg-blue-100', text: 'text-blue-800' },
            cancelled: { label: 'Cancelado', bg: 'bg-gray-100', text: 'text-gray-800' },
        };

        // Verificar se vence hoje (independente do status no banco)
        let statusFinal = status;
        if (!STATUS_FINALIZADOS.includes(status) && dataVencimento) {
            const hoje = new Date();
            hoje.setHours(0, 0, 0, 0);
            // Parse como data local (evita problema de timezone UTC vs BR)
            const partes = String(dataVencimento).split('T')[0].split('-');
            const vencimento = new Date(parseInt(partes[0]), parseInt(partes[1]) - 1, parseInt(partes[2]));

            if (vencimento.getTime() === hoje.getTime()) {
                return (
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                        Vence Hoje
                    </span>
                );
            }
            if ((status === 'open' || status === 'pending') && vencimento.getTime() < hoje.getTime()) {
                statusFinal = 'overdue';
            }
        }

        const config = statusConfig[statusFinal] || { label: statusFinal || 'Desconhecido', bg: 'bg-gray-100', text: 'text-gray-800' };
        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
                {config.label}
            </span>
        );
    };

    // Colunas da tabela
    const [columns, setColumns] = useState([
        {
            id: "id",
            label: "ID",
            visible: true,
            formatter: (value, doc) => (
                <span 
                    className="text-stone-600 hover:underline cursor-pointer"
                    onClick={() => handleShowDetalhes(doc)}
                >
                    {value}
                </span>
            ),
        },
        {
            id: "beneficiarioNome",
            label: "Beneficiário",
            visible: true,
            formatter: (value, doc) => doc.beneficiarioNomeFormatted || "-",
        },
        {
            id: "dataVencimento",
            label: "Vencimento",
            visible: true,
            formatter: (value, doc) => doc.dataVencimentoFormatted || "-",
        },
        {
            id: "valor",
            label: "Valor",
            visible: true,
            formatter: (value, doc) => doc.valorFormatted,
        },
        {
            id: "status",
            label: "Status",
            visible: true,
            formatter: (value, doc) => doc.statusBadgeFormatted,
        },
        {
            id: "notaFiscal",
            label: "Nota Vinculada",
            visible: true,
            formatter: (value, doc) => {
                // Verifica se tem NFe de Material vinculada
                if (doc.notaFiscal) {
                    return doc.notaFiscal.pdf ? (
                        <button
                            onClick={() => handleOpenNfePdf(doc.notaFiscal.pdf)}
                            className="text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                            title="NFe Material - Clique para abrir o PDF"
                        >
                            <img className="w-4 h-4" src="images/icon/pdf.png" alt="PDF" />
                            <span className="text-xs bg-blue-100 px-1 rounded">MAT</span> #{doc.notaFiscal.numero}
                        </button>
                    ) : (
                        <span className="text-blue-600 flex items-center gap-1" title="NFe Material - PDF não disponível">
                            <span className="text-xs bg-blue-100 px-1 rounded">MAT</span> #{doc.notaFiscal.numero}
                        </span>
                    );
                }
                
                // Verifica se tem NFSe (Serviço) vinculada
                if (doc.notaFiscalServico) {
                    return doc.notaFiscalServico.pdf ? (
                        <button
                            onClick={() => handleOpenNfePdf(doc.notaFiscalServico.pdf)}
                            className="text-green-600 hover:text-green-800 hover:underline flex items-center gap-1"
                            title="NFSe - Clique para abrir o PDF"
                        >
                            <img className="w-4 h-4" src="images/icon/pdf.png" alt="PDF" />
                            <span className="text-xs bg-green-100 px-1 rounded">SER</span> #{doc.notaFiscalServico.numero}
                        </button>
                    ) : (
                        <span className="text-green-600 flex items-center gap-1" title="NFSe - PDF não disponível">
                            <span className="text-xs bg-green-100 px-1 rounded">SER</span> #{doc.notaFiscalServico.numero}
                        </span>
                    );
                }
                
                // Nenhuma nota vinculada
                return <span className="text-gray-400">-</span>;
            },
        },
        {
            id: "digitableLine",
            label: "Código de Barras",
            visible: true,
            formatter: (value, doc) => (
                <button
                    onClick={() => handleCopyCodigoBarras(value)}
                    className="px-2 py-1 bg-stone-100 hover:bg-stone-200 rounded text-xs flex items-center gap-1"
                    title="Clique para copiar"
                >
                    <img className="w-4 h-4" src="images/icon/files.png" alt="Copiar" />
                    Copiar
                </button>
            ),
        },
        {
            id: "acoes",
            label: "Ações",
            visible: true,
            formatter: (value, doc) => (
                <div className="flex gap-2">
                    {podeVisualizar && (
                        <button
                            onClick={() => handleShowDetalhes(doc)}
                            className="p-1 hover:bg-stone-100 rounded"
                            title="Ver detalhes"
                        >
                            <img className="w-5 h-5" src="images/icon/search-file.png" alt="Ver" />
                        </button>
                    )}
                    {podeAtualizar && (
                        <button
                            onClick={() => handleShowStatusModal(doc)}
                            className="p-1 hover:bg-stone-100 rounded"
                            title="Atualizar status"
                        >
                            <img className="w-5 h-5" src="images/icon/edit.png" alt="Editar" />
                        </button>
                    )}
                    {podeAtualizar && (
                        <button
                            onClick={() => handleShowVincularModal(doc)}
                            className="p-1 hover:bg-stone-100 rounded"
                            title="Vincular/Desvincular Nota Fiscal"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                            </svg>
                        </button>
                    )}
                    {podeAtualizar && (
                        <button
                            onClick={() => handleShowAnexosModal(doc)}
                            className="p-1 hover:bg-stone-100 rounded"
                            title="Anexar Documentos"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                            </svg>
                        </button>
                    )}
                    {podeVisualizar && doc.pdfLocal && (
                        <button
                            onClick={() => handleShowPdf(doc)}
                            className="p-1 hover:bg-stone-100 rounded"
                            title="Ver PDF"
                        >
                            <img className="w-5 h-5" src="images/icon/pdf.png" alt="PDF" />
                        </button>
                    )}
                </div>
            ),
        },
    ]);

    // Função para pré-processar dados e formatar uma única vez
    const preprocessData = useCallback((records) => {
        const startTime = performance.now();
        
        const processedData = records.map(boleto => ({
            ...boleto,
            // Pré-formatar beneficiário
            beneficiarioNomeFormatted: boleto.beneficiarioNome?.toUpperCase() || "-",
            // Pré-formatar data de vencimento
            dataVencimentoFormatted: boleto.dataVencimento ? formatDateWithoutHour(boleto.dataVencimento) : "-",
            // Pré-formatar valor
            valorFormatted: formatCurrency(boleto.valor),
            // Pré-formatar status badge
            statusBadgeFormatted: getStatusBadge(boleto.status, boleto.dataVencimento),
        }));
        
        const endTime = performance.now();
        console.log(`⚡ Pré-processamento Boletos: ${(endTime - startTime).toFixed(2)}ms (${records.length} registros)`);
        
        return processedData;
    }, []);

    // Buscar boletos
    // Descarta respostas de filtros que já foram superados por outro mais novo:
    // sem isto, a resposta lenta de um filtro anterior sobrescrevia a tela.
    const requisicaoMaisRecente = useRequisicaoMaisRecente();

    const fetchBoletos = async (page = 1, size = 10, filtrosParaUsar = filters) => {
        const apiStartTime = performance.now();
        
        try {
            setLoading(true);
            renderStartTimeRef.current = performance.now();
            
            // Aviso para grandes datasets
            if (size > 1000) {
                console.warn(`⚠️ Carregando ${size} registros por página - pode demorar mais que o esperado`);
            }
            
            const params = new URLSearchParams({
                page,
                pageSize: size,
                ...Object.fromEntries(
                    Object.entries(filtrosParaUsar).filter(([_, v]) => v !== "")
                ),
            });
            
            const idRequisicao = requisicaoMaisRecente.iniciar();
            
            const response = await api.get(`/boletos?${params}`);
            
            // Chegou atrasada e ja foi superada por outro filtro: descarta.
            
            if (!requisicaoMaisRecente.ehAtual(idRequisicao)) return;

            
            const apiEndTime = performance.now();
            const apiTime = apiEndTime - apiStartTime;
            console.log(`🌐 API Boletos: ${apiTime.toFixed(2)}ms (${response.data.records?.length || 0} registros)`);
            
            // Pré-processar dados
            const processedRecords = preprocessData(response.data.records || []);
            
            preRenderTimeRef.current = performance.now();
            
            setBoletos(processedRecords);
            setTotalRecords(response.data.total || 0);
            
            const stateUpdateTime = performance.now();
            console.log(`📊 State update Boletos: ${(stateUpdateTime - preRenderTimeRef.current).toFixed(2)}ms`);
            
            // Aguardar renderização completa
            setTimeout(() => {
                const totalTime = performance.now() - renderStartTimeRef.current;
                const pureRenderTime = performance.now() - preRenderTimeRef.current;
                const numCells = (response.data.records?.length || 0) * 8; // 8 colunas visíveis por padrão
                const timePerCell = pureRenderTime / numCells;
                
                console.log(`🎨 Renderização pura Boletos: ${pureRenderTime.toFixed(2)}ms`);
                console.log(`⏱️ Tempo total Boletos: ${totalTime.toFixed(2)}ms`);
                console.log(`📄 Células renderizadas: ${numCells.toLocaleString()} (${response.data.records?.length || 0} linhas × 8 colunas)`);
                console.log(`⚡ Tempo por célula: ${timePerCell.toFixed(2)}ms`);
                
                // Análise automática
                console.log("\n📊 === ANÁLISE DE PERFORMANCE BOLETOS ===");
                console.log(`API: ${apiTime.toFixed(0)}ms (${((apiTime/totalTime)*100).toFixed(1)}%)`);
                console.log(`Pré-processamento: ${(preRenderTimeRef.current - apiEndTime).toFixed(0)}ms (${(((preRenderTimeRef.current - apiEndTime)/totalTime)*100).toFixed(1)}%)`);
                console.log(`State Update: ${(stateUpdateTime - preRenderTimeRef.current).toFixed(2)}ms (${(((stateUpdateTime - preRenderTimeRef.current)/totalTime)*100).toFixed(1)}%)`);
                console.log(`Renderização DOM: ${pureRenderTime.toFixed(0)}ms (${((pureRenderTime/totalTime)*100).toFixed(1)}%)`);
                
                if (pureRenderTime > 5000) {
                    console.warn("⚠️ CRÍTICO: Renderização muito lenta (>5s)! Considere:");
                    console.warn("  - Reduzir registros por página");
                    console.warn("  - Implementar virtualização (react-window)");
                    console.warn("  - Simplificar colunas/formatações");
                } else if (pureRenderTime > 2000) {
                    console.warn("⚠️ ATENÇÃO: Renderização lenta (>2s). Considere otimizar.");
                }
                
                setLoading(false);
            }, 100);
            
        } catch (error) {
            console.error("Erro ao carregar boletos:", error);
            setMessage("Erro ao carregar boletos");
            setShowSuccessDialog(true);
            setLoading(false);
        }
    };

    // Buscar estatísticas
    const fetchEstatisticas = async () => {
        try {
            const response = await api.get('/boletos/estatisticas');
            setEstatisticas(response.data);
        } catch (error) {
            console.error("Erro ao carregar estatísticas:", error);
        }
    };

    // Filtrar por card clicado
    const handleCardFilter = (cardType) => {
        // Se clicar no mesmo card, limpa o filtro
        if (activeCard === cardType) {
            const vazios = {
                status: "",
                dataVencimentoInicial: "",
                dataVencimentoFinal: "",
                beneficiario: "",
                vinculadoNfe: "",
            };
            setActiveCard(null);
            setFilters(vazios);
            setAppliedFilters(vazios);
            setCurrentPage(1);
            return;
        }

        setActiveCard(cardType);
        const hoje = new Date();
        const hojeStr = hoje.toISOString().split('T')[0];
        
        // Próximos 7 dias
        const proximos7Dias = new Date(hoje);
        proximos7Dias.setDate(proximos7Dias.getDate() + 7);
        const proximos7DiasStr = proximos7Dias.toISOString().split('T')[0];

        let newFilters = {
            status: "",
            dataVencimentoInicial: "",
            dataVencimentoFinal: "",
            beneficiario: "",
            vinculadoNfe: "",
        };

        switch (cardType) {
            case 'total':
                // Sem filtros, mostra todos
                break;
            case 'pendentes':
                // Pendentes = open/pending MAS apenas os que ainda não venceram (dataVencimento >= hoje)
                newFilters.status = 'open,pending';
                newFilters.dataVencimentoInicial = hojeStr;  // Só os que vencem de hoje em diante
                break;
            case 'pagos':
                newFilters.status = 'paid';
                break;
            case 'vencidos':
                newFilters.status = 'overdue';
                break;
            case 'vencemHoje':
                newFilters.status = 'open,pending';
                newFilters.dataVencimentoInicial = hojeStr;
                newFilters.dataVencimentoFinal = hojeStr;
                break;
            case 'proximos7Dias':
                newFilters.status = 'open,pending';
                newFilters.dataVencimentoInicial = hojeStr;
                newFilters.dataVencimentoFinal = proximos7DiasStr;
                break;
            default:
                break;
        }

        setFilters(newFilters);
        setAppliedFilters(newFilters);
        setCurrentPage(1);
    };

    // Effects
    // Depende dos filtros APLICADOS, nao do que esta sendo digitado: antes o effect
    // observava `filters`, entao cada tecla digitada no painel disparava uma
    // requisicao e a lista mudava embaixo do usuario antes de ele clicar em "Filtrar".
    useEffect(() => {
        fetchBoletos(currentPage, pageSize, appliedFilters);
        fetchEstatisticas();
    }, [currentPage, pageSize, appliedFilters]);

    // Handlers
    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleCopyCodigoBarras = async (codigo) => {
        try {
            await navigator.clipboard.writeText(codigo);
            setMessage("Código de barras copiado!");
            setShowSuccessDialog(true);
        } catch (error) {
            console.error("Erro ao copiar:", error);
            setMessage("Erro ao copiar código de barras");
            setShowSuccessDialog(true);
        }
    };

    const handleShowDetalhes = async (boleto) => {
        try {
            const response = await api.get(`/boletos/${boleto.id}`);
            setSelectedBoleto(response.data);
            setShowDetalheModal(true);
        } catch (error) {
            console.error("Erro ao carregar detalhes:", error);
            setMessage("Erro ao carregar detalhes do boleto");
            setShowSuccessDialog(true);
        }
    };

    const handleShowPdf = async (boleto) => {
        if (boleto.pdfLocal) {
            setSelectedPdfUrl(config.urlArquivo(boleto.pdfLocal));
            setShowPdfModal(true);
        } else if (boleto.pdfUrl || boleto.pagadorDocumento) {
            // Baixar da API Qive
            try {
                setLoading(true);
                const response = await api.get(`/boletos/${boleto.id}/pdf`, {
                    responseType: 'blob'
                });
                const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                setSelectedPdfUrl(url);
                setShowPdfModal(true);
            } catch (error) {
                console.error("Erro ao baixar PDF:", error);
                if (error.response && error.response.status === 404) {
                    setMessage("PDF não disponível. O boleto pode não ter PDF na plataforma Qive.");
                } else {
                    setMessage("Erro ao baixar PDF do boleto. Tente novamente.");
                }
                setShowSuccessDialog(true);
            } finally {
                setLoading(false);
            }
        } else {
            setMessage("PDF não disponível para este boleto");
            setShowSuccessDialog(true);
        }
    };

    // Abrir PDF da NFe vinculada (usando modal)
    const handleOpenNfePdf = (pdfPath) => {
        if (!pdfPath) {
            setMessage("PDF da NFe não disponível");
            setShowSuccessDialog(true);
            return;
        }
        
        // Abrir PDF no modal usando a URL base de arquivos
        setSelectedPdfUrl(config.urlArquivo(pdfPath));
        setShowPdfModal(true);
    };

    const handleShowStatusModal = (boleto) => {
        setSelectedBoleto(boleto);
        setStatusForm({
            status: boleto.status || 'open',
            dataPagamento: boleto.dataPagamento ? boleto.dataPagamento.split('T')[0] : '',
            observacao: boleto.observacao || ''
        });
        setShowStatusModal(true);
    };

    const handleAtualizarStatus = async () => {
        try {
            await api.put(`/boletos/${selectedBoleto.id}/status`, statusForm);
            setMessage("Status atualizado com sucesso!");
            setShowSuccessDialog(true);
            setShowStatusModal(false);
            fetchBoletos(currentPage, pageSize);
            fetchEstatisticas();
        } catch (error) {
            console.error("Erro ao atualizar status:", error);
            setMessage("Erro ao atualizar status");
            setShowSuccessDialog(true);
        }
    };
    
    // ==========================================
    // FUNÇÕES DE VINCULAÇÃO DE NOTA FISCAL
    // ==========================================
    
    const handleShowVincularModal = async (boleto) => {
        setSelectedBoleto(boleto);
        setShowVincularModal(true);
        setSearchNotaFiscal('');
        setNotaSelecionadaVinculo(null);
        
        // Se o boleto já tem uma nota vinculada, buscar as informações
        if (boleto.notaFiscalId || boleto.notaFiscalServicoId) {
            try {
                const response = await api.get(`/boletos/${boleto.id}`);
                setSelectedBoleto(response.data);
            } catch (error) {
                console.error("Erro ao carregar detalhes do boleto:", error);
            }
        }
        
        // Buscar notas iniciais do beneficiário do boleto
        if (boleto.beneficiarioDocumento) {
            buscarNotasFiscais('', boleto.beneficiarioDocumento);
        }
    };
    
    const buscarNotasFiscais = async (search = '', beneficiarioDocumento = '') => {
        try {
            setBuscandoNotas(true);
            const params = new URLSearchParams({
                search: search || '',
                limit: 20,
                tipo: tipoNotaVinculo,
                beneficiarioDocumento: beneficiarioDocumento || selectedBoleto?.beneficiarioDocumento || ''
            });
            
            const response = await api.get(`/boletos/notas-fiscais?${params}`);
            setNotasFiscaisDisponiveis(response.data);
        } catch (error) {
            console.error("Erro ao buscar notas fiscais:", error);
            setMessage("Erro ao buscar notas fiscais");
            setShowSuccessDialog(true);
        } finally {
            setBuscandoNotas(false);
        }
    };
    
    const handleSearchNotaFiscal = (e) => {
        const value = e.target.value;
        setSearchNotaFiscal(value);
        
        // Debounce: buscar após parar de digitar
        if (window.searchNotaTimeout) {
            clearTimeout(window.searchNotaTimeout);
        }
        
        window.searchNotaTimeout = setTimeout(() => {
            buscarNotasFiscais(value);
        }, 500);
    };
    
    const handleChangeTipoNota = (tipo) => {
        setTipoNotaVinculo(tipo);
        setTimeout(() => {
            buscarNotasFiscais(searchNotaFiscal);
        }, 100);
    };
    
    const handleVincularNota = async () => {
        if (!notaSelecionadaVinculo) {
            setMessage("Selecione uma nota fiscal para vincular");
            setShowSuccessDialog(true);
            return;
        }
        
        try {
            const payload = {
                tipo: notaSelecionadaVinculo.tipo,
            };
            
            if (notaSelecionadaVinculo.tipo === 'material') {
                payload.notaFiscalId = notaSelecionadaVinculo.id;
                payload.notaFiscalServicoId = null;
            } else if (notaSelecionadaVinculo.tipo === 'servico') {
                payload.notaFiscalServicoId = notaSelecionadaVinculo.id;
                payload.notaFiscalId = null;
            }
            
            await api.put(`/boletos/${selectedBoleto.id}/vincular-nfe`, payload);
            
            setMessage("Nota fiscal vinculada com sucesso!");
            setShowSuccessDialog(true);
            setShowVincularModal(false);
            fetchBoletos(currentPage, pageSize);
        } catch (error) {
            console.error("Erro ao vincular nota fiscal:", error);
            setMessage(error.response?.data?.error || "Erro ao vincular nota fiscal");
            setShowSuccessDialog(true);
        }
    };
    
    const handleDesvincularNota = async () => {
        if (!window.confirm('Deseja realmente remover o vínculo com esta nota fiscal?')) {
            return;
        }
        
        try {
            await api.put(`/boletos/${selectedBoleto.id}/vincular-nfe`, {
                tipo: null,
                notaFiscalId: null,
                notaFiscalServicoId: null
            });
            
            setMessage("Vínculo removido com sucesso!");
            setShowSuccessDialog(true);
            setShowVincularModal(false);
            fetchBoletos(currentPage, pageSize);
        } catch (error) {
            console.error("Erro ao desvincular nota fiscal:", error);
            setMessage("Erro ao remover vínculo");
            setShowSuccessDialog(true);
        }
    };
    
    // ==========================================
    // FIM DAS FUNÇÕES DE VINCULAÇÃO
    // ==========================================

    // ==========================================
    // FUNÇÕES DE ANEXOS/DOCUMENTOS
    // ==========================================

    const fetchTiposDocumentos = async () => {
        try {
            const response = await api.get('/tiposdocumentos');
            setTiposDocumentos(response.data);
        } catch (error) {
            console.error("Erro ao carregar tipos de documentos:", error);
        }
    };

    const fetchAnexosBoleto = async (boletoId) => {
        try {
            setLoadingAnexos(true);
            const response = await api.get(`/boletos/${boletoId}/anexos`);
            setAnexosBoleto(response.data);
        } catch (error) {
            console.error("Erro ao carregar anexos:", error);
            setMessage("Erro ao carregar documentos do boleto");
            setShowSuccessDialog(true);
        } finally {
            setLoadingAnexos(false);
        }
    };

    const handleShowAnexosModal = async (boleto) => {
        setSelectedBoleto(boleto);
        setShowAnexosModal(true);
        setAnexoTipoId('');
        setAnexoFile(null);

        if (tiposDocumentos.length === 0) {
            await fetchTiposDocumentos();
        }
        await fetchAnexosBoleto(boleto.id);
    };

    const handleAddAnexo = async () => {
        if (!anexoTipoId) {
            setMessage("Selecione um tipo de documento");
            setShowSuccessDialog(true);
            return;
        }
        if (!anexoFile) {
            setMessage("Selecione um arquivo");
            setShowSuccessDialog(true);
            return;
        }

        try {
            setEnviandoAnexo(true);
            const formData = new FormData();
            formData.append('tipoId', anexoTipoId);
            formData.append('file', anexoFile, anexoFile.name);

            await api.post(`/boletos/${selectedBoleto.id}/anexos`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            setMessage("Documento anexado com sucesso!");
            setShowSuccessDialog(true);
            setAnexoTipoId('');
            setAnexoFile(null);
            await fetchAnexosBoleto(selectedBoleto.id);
        } catch (error) {
            console.error("Erro ao anexar documento:", error);
            setMessage(error.response?.data?.error || "Erro ao anexar documento");
            setShowSuccessDialog(true);
        } finally {
            setEnviandoAnexo(false);
        }
    };

    const handleRemoveAnexo = async (anexoId) => {
        if (!window.confirm('Deseja realmente remover este documento?')) {
            return;
        }

        try {
            await api.delete(`/boletos/${selectedBoleto.id}/anexos/${anexoId}`);
            setMessage("Documento removido com sucesso!");
            setShowSuccessDialog(true);
            await fetchAnexosBoleto(selectedBoleto.id);
        } catch (error) {
            console.error("Erro ao remover documento:", error);
            setMessage("Erro ao remover documento");
            setShowSuccessDialog(true);
        }
    };

    const handleViewAnexo = (arquivo) => {
        if (arquivo) {
            setSelectedPdfUrl(config.urlArquivo(arquivo));
            setShowPdfModal(true);
        }
    };

    // ==========================================
    // FIM DAS FUNÇÕES DE ANEXOS
    // ==========================================

    const handleSincronizar = async () => {
        try {
            setSincronizando(true);
            const response = await api.post('/boletos/sincronizar', {});
            setMessage(`Sincronização concluída! ${response.data.novos} novos, ${response.data.atualizados} atualizados.`);
            setShowSuccessDialog(true);
            fetchBoletos(currentPage, pageSize);
            fetchEstatisticas();
        } catch (error) {
            console.error("Erro ao sincronizar:", error);
            setMessage(error.response?.data?.error || "Erro ao sincronizar boletos");
            setShowSuccessDialog(true);
        } finally {
            setSincronizando(false);
        }
    };

    const applyFilters = () => {
        setAppliedFilters({ ...filters });
        setCurrentPage(1);
    };

    const clearFilters = () => {
        const vazios = {
            status: "",
            dataVencimentoInicial: "",
            dataVencimentoFinal: "",
            beneficiario: "",
            vinculadoNfe: "",
        };
        clearPersistedState();
        setFilters(vazios);
        setAppliedFilters(vazios);
        setActiveCard(null);
        setCurrentPage(1);
    };

    const toggleColumnVisibility = (id) => {
        setColumns((prevColumns) =>
            prevColumns.map((col) =>
                col.id === id ? { ...col, visible: !col.visible } : col
            )
        );
    };

    const handleMenuClick = (event) => {
        event.stopPropagation();
    };

    const exportToExcel = async () => {
        try {
            setLoading(true);
            
            // Buscar TODOS os dados com os filtros atuais (sem paginação)
            const params = new URLSearchParams({
                page: 1,
                pageSize: 10000, // Buscar todos
                ...Object.fromEntries(
                    Object.entries(filters).filter(([_, v]) => v !== "")
                ),
            });
            
            const response = await api.get(`/boletos?${params}`);
            const allBoletos = response.data.records || [];
            
            if (allBoletos.length === 0) {
                setMessage('Não há dados para exportar!');
                setShowSuccessDialog(true);
                return;
            }

            const dataToExport = allBoletos.map((boleto) => ({
                'ID': boleto.id,
                'Beneficiário': boleto.beneficiarioNome,
                'CNPJ Beneficiário': boleto.beneficiarioDocumento,
                'Pagador': boleto.pagadorNome,
                'CNPJ Pagador': boleto.pagadorDocumento,
                'Vencimento': boleto.dataVencimento ? formatDateWithoutHour(boleto.dataVencimento) : '',
                'Valor': boleto.valor ? parseFloat(boleto.valor) : 0,
                'Status': getStatusLabel(boleto.status, boleto.dataVencimento),
                'Linha Digitável': boleto.digitableLine,
                'Código de Barras': boleto.barCode,
                'NFe Vinculada': boleto.notaFiscal?.numero || '',
            }));

            const worksheet = XLSX.utils.json_to_sheet(dataToExport);
            
            // Formatar coluna de Valor como moeda (coluna H = índice 7)
            const range = XLSX.utils.decode_range(worksheet['!ref']);
            for (let row = range.s.r + 1; row <= range.e.r; row++) {
                const cellAddress = XLSX.utils.encode_cell({ r: row, c: 7 }); // Coluna H (Valor)
                if (worksheet[cellAddress]) {
                    worksheet[cellAddress].z = '"R$" #,##0.00'; // Formato moeda brasileira
                }
            }
            
            // Ajustar largura das colunas
            worksheet['!cols'] = [
                { wch: 8 },   // ID
                { wch: 35 },  // Beneficiário
                { wch: 18 },  // CNPJ Beneficiário
                { wch: 35 },  // Pagador
                { wch: 18 },  // CNPJ Pagador
                { wch: 12 },  // Vencimento
                { wch: 15 },  // Valor
                { wch: 12 },  // Status
                { wch: 50 },  // Linha Digitável
                { wch: 50 },  // Código de Barras
                { wch: 15 },  // NFe Vinculada
            ];
            
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Boletos");
            
            // Nome do arquivo com data
            const hoje = new Date().toISOString().split('T')[0];
            XLSX.writeFile(workbook, `boletos_${hoje}.xlsx`);
            
        } catch (error) {
            console.error("Erro ao exportar:", error);
            setMessage("Erro ao exportar boletos");
            setShowSuccessDialog(true);
        } finally {
            setLoading(false);
        }
    };

    // Paginação
    const totalPages = Math.ceil(totalRecords / pageSize);
    const startRecord = (currentPage - 1) * pageSize + 1;
    const endRecord = Math.min(currentPage * pageSize, totalRecords);
    const pageWindowSize = 5;
    // Mantem a janela cheia tambem nas ultimas paginas (antes ela encolhia).
    const startPage = Math.max(1, Math.min(currentPage - Math.floor(pageWindowSize / 2), totalPages - pageWindowSize + 1));
    const endPage = Math.min(totalPages, startPage + pageWindowSize - 1);
    const pageNumbers = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);

    return (
        <Template>
            <div className="h-auto w-full p-4 md:p-8 flex flex-col gap-6">
                {/* Cabeçalho */}
                <div className="flex flex-wrap justify-between items-center gap-4">
                    <div className="text-stone-800 text-xl md:text-2xl font-medium font-['Inter']">Boletos</div>
                    <div className="flex gap-2">
                        {podeSincronizar && (
                            <button
                                onClick={handleSincronizar}
                                disabled={sincronizando}
                                className={`px-4 py-2 rounded-md justify-center items-center gap-2 flex ${
                                    sincronizando 
                                        ? 'bg-gray-400 cursor-not-allowed' 
                                        : 'bg-stone-600 hover:bg-stone-700'
                                }`}
                            >
                                <img className="w-4 h-4" src="images/icon/download.png" alt="Sincronizar" style={{ filter: 'brightness(0) invert(1)' }} />
                                <span className="text-white text-sm font-medium">
                                    {sincronizando ? 'Sincronizando...' : 'Sincronizar'}
                                </span>
                            </button>
                        )}
                    </div>
                </div>

                {/* Cards de Estatísticas - Clicáveis para filtrar */}
                {estatisticas && (
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                        <div 
                            onClick={() => handleCardFilter('total')}
                            className={`rounded-lg border p-4 cursor-pointer transition-all hover:shadow-md ${
                                activeCard === 'total' 
                                    ? 'bg-gray-200 border-gray-400 ring-2 ring-gray-400' 
                                    : 'bg-white border-gray-200 hover:border-gray-300'
                            }`}
                        >
                            <div className="text-sm text-gray-500">Total</div>
                            <div className="text-2xl font-bold text-gray-800">{estatisticas.total}</div>
                        </div>
                        <div 
                            onClick={() => handleCardFilter('pendentes')}
                            className={`rounded-lg border p-4 cursor-pointer transition-all hover:shadow-md ${
                                activeCard === 'pendentes' 
                                    ? 'bg-yellow-200 border-yellow-400 ring-2 ring-yellow-400' 
                                    : 'bg-yellow-50 border-yellow-200 hover:border-yellow-300'
                            }`}
                        >
                            <div className="text-sm text-yellow-600">Pendentes</div>
                            <div className="text-2xl font-bold text-yellow-800">{estatisticas.porStatus?.pendentes || 0}</div>
                        </div>
                        <div 
                            onClick={() => handleCardFilter('pagos')}
                            className={`rounded-lg border p-4 cursor-pointer transition-all hover:shadow-md ${
                                activeCard === 'pagos' 
                                    ? 'bg-green-200 border-green-400 ring-2 ring-green-400' 
                                    : 'bg-green-50 border-green-200 hover:border-green-300'
                            }`}
                        >
                            <div className="text-sm text-green-600">Pagos</div>
                            <div className="text-2xl font-bold text-green-800">{estatisticas.porStatus?.pagos || 0}</div>
                        </div>
                        <div 
                            onClick={() => handleCardFilter('vencidos')}
                            className={`rounded-lg border p-4 cursor-pointer transition-all hover:shadow-md ${
                                activeCard === 'vencidos' 
                                    ? 'bg-red-200 border-red-400 ring-2 ring-red-400' 
                                    : 'bg-red-50 border-red-200 hover:border-red-300'
                            }`}
                        >
                            <div className="text-sm text-red-600">Vencidos</div>
                            <div className="text-2xl font-bold text-red-800">{estatisticas.porStatus?.vencidos || 0}</div>
                        </div>
                        <div 
                            onClick={() => handleCardFilter('vencemHoje')}
                            className={`rounded-lg border p-4 cursor-pointer transition-all hover:shadow-md ${
                                activeCard === 'vencemHoje' 
                                    ? 'bg-orange-200 border-orange-400 ring-2 ring-orange-400' 
                                    : 'bg-orange-50 border-orange-200 hover:border-orange-300'
                            }`}
                        >
                            <div className="text-sm text-orange-600">Vencem Hoje</div>
                            <div className="text-2xl font-bold text-orange-800">{estatisticas.alertas?.vencemHoje || 0}</div>
                        </div>
                        <div 
                            onClick={() => handleCardFilter('proximos7Dias')}
                            className={`rounded-lg border p-4 cursor-pointer transition-all hover:shadow-md ${
                                activeCard === 'proximos7Dias' 
                                    ? 'bg-blue-200 border-blue-400 ring-2 ring-blue-400' 
                                    : 'bg-blue-50 border-blue-200 hover:border-blue-300'
                            }`}
                        >
                            <div className="text-sm text-blue-600">Próximos 7 dias</div>
                            <div className="text-2xl font-bold text-blue-800">{estatisticas.alertas?.vencemProximos7Dias || 0}</div>
                        </div>
                    </div>
                )}

                {/* Barra de Ferramentas */}
                <div className="flex flex-wrap gap-2 md:gap-4 items-center">
                    <div className="flex-grow min-w-0 flex items-center gap-2">
                        <div className="text-stone-600 text-sm font-medium">Ver</div>
                        <select
                            className="w-20 h-8 pl-2 pr-2 py-1 bg-white rounded-md border border-slate-300 text-sm text-slate-700"
                            value={pageSize}
                            onChange={(e) => {
                                const newPageSize = parseInt(e.target.value, 10);
                                setPageSize(newPageSize);
                                setCurrentPage(1);
                                fetchBoletos(1, newPageSize);
                            }}
                            disabled={loading}
                        >
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                            <option value={150}>150</option>
                            <option value={200}>200</option>
                            <option value={500}>500</option>
                            <option value={1000}>1000</option>
                            <option value={2000}>2000</option>
                        </select>
                        <div className="text-stone-600 text-sm font-medium">Resultados</div>
                    </div>

                    <div className="flex flex-wrap gap-2 flex-none w-full md:w-auto">
                        {/* Botão Colunas */}
                        <div ref={buttonRef} className={`relative justify-end items-center gap-1 flex ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`} onClick={() => !loading && setMenuOpen(menuOpen === "colunas" ? null : "colunas")}>
                            <div className="justify-start items-center gap-2 flex">
                                <img className="w-6 h-6 relative" src="images/icon/columns.png" alt="Colunas" />
                                <div className="text-stone-600 text-sm font-medium">Colunas</div>
                            </div>
                            <img className="w-6 h-4 px-1 justify-center items-center flex" src="images/icon/chevron-down.png" alt="" />
                            {menuOpen === "colunas" && (
                                <div className="absolute top-full left-0 mt-2 w-48 bg-white border border-gray-300 rounded-md shadow-lg z-50" onClick={handleMenuClick}>
                                    <div className="p-4 flex flex-col gap-3">
                                        {columns.map((col) => (
                                            <div key={col.id} className="flex items-center gap-2">
                                                <input
                                                    type="checkbox"
                                                    id={col.id}
                                                    checked={col.visible}
                                                    onChange={() => toggleColumnVisibility(col.id)}
                                                />
                                                <label htmlFor={col.id} className="text-sm text-gray-700">{col.label}</label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Botão Filtrar */}
                        <div ref={buttonFiltrarRef} className="relative justify-start items-center gap-1 flex cursor-pointer" onClick={() => setMenuOpen(menuOpen === "filtro" ? null : "filtro")}>
                            <div className="justify-start items-center gap-2 flex">
                                <img className="w-8 h-8 px-0.5 py-[3px] justify-center items-center flex" src="images/icon/filter.png" alt="Filtrar" />
                                <div className="text-stone-600 text-sm font-medium">Filtrar</div>
                            </div>
                            <img className="w-6 h-4 px-1 justify-center items-center flex" src="images/icon/chevron-down.png" alt="" />
                            
                            {menuOpen === "filtro" && (
                                <div className="absolute top-full left-0 mt-2 w-[350px] bg-white rounded-md shadow-lg z-50 p-4 border border-gray-300" style={{ zIndex: 9999 }} onClick={handleMenuClick}>
                                    <div className="flex flex-col gap-4">
                                        <div className="text-stone-800 text-xl font-medium">Filtrar</div>
                                        
                                        <div className="flex flex-col gap-1.5">
                                            <div className="text-slate-700 text-sm font-medium">Status</div>
                                            <select
                                                className="w-full px-3 py-2 bg-white rounded-md border border-slate-300 text-slate-700"
                                                value={filters.status}
                                                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                                            >
                                                <option value="">Todos</option>
                                                <option value="open">Em Aberto</option>
                                                <option value="paid">Pago</option>
                                                <option value="overdue">Vencido</option>
                                                <option value="closed">Baixado</option>
                                                <option value="canceled">Cancelado</option>
                                            </select>
                                        </div>

                                        <div className="flex flex-col gap-1.5">
                                            <div className="text-slate-700 text-sm font-medium">Beneficiário</div>
                                            <input
                                                type="text"
                                                className="w-full px-3 py-2 bg-white rounded-md border border-slate-300 text-slate-700"
                                                placeholder="Nome ou CNPJ"
                                                value={filters.beneficiario}
                                                onChange={(e) => setFilters({ ...filters, beneficiario: e.target.value })}
                                            />
                                        </div>

                                        <div className="flex flex-col gap-1.5">
                                            <div className="text-slate-700 text-sm font-medium">Vencimento</div>
                                            <div className="flex gap-2">
                                                <input
                                                    type="date"
                                                    className="w-1/2 px-3 py-2 bg-white rounded-md border border-slate-300 text-slate-700"
                                                    value={filters.dataVencimentoInicial}
                                                    onChange={(e) => setFilters({ ...filters, dataVencimentoInicial: e.target.value })}
                                                />
                                                <input
                                                    type="date"
                                                    className="w-1/2 px-3 py-2 bg-white rounded-md border border-slate-300 text-slate-700"
                                                    value={filters.dataVencimentoFinal}
                                                    onChange={(e) => setFilters({ ...filters, dataVencimentoFinal: e.target.value })}
                                                />
                                            </div>
                                        </div>

                                        <div className="flex flex-col gap-1.5">
                                            <div className="text-slate-700 text-sm font-medium">Vinculado a NFe</div>
                                            <select
                                                className="w-full px-3 py-2 bg-white rounded-md border border-slate-300 text-slate-700"
                                                value={filters.vinculadoNfe}
                                                onChange={(e) => setFilters({ ...filters, vinculadoNfe: e.target.value })}
                                            >
                                                <option value="">Todos</option>
                                                <option value="true">Sim</option>
                                                <option value="false">Não</option>
                                            </select>
                                        </div>

                                        <div className="flex items-center justify-between gap-4 mt-4">
                                            <button className="px-4 py-2 w-full bg-white rounded-md border border-stone-500 flex justify-center items-center gap-2" onClick={clearFilters}>
                                                <img className="w-4 h-4" src="images/icon/eraser.png" alt="" />
                                                <span className="text-stone-500 text-sm font-medium">Limpar</span>
                                            </button>
                                            <button className="px-4 py-2 w-full bg-stone-600 rounded-md flex justify-center items-center gap-2" onClick={applyFilters}>
                                                <img className="w-4 h-4" src="images/icon/filter-white.png" alt="" />
                                                <span className="text-white text-sm font-medium">Filtrar</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {podeExportar && (
                        <button className="flex px-4 py-2 bg-white/40 rounded-md border border-stone-600 justify-center items-center gap-2 cursor-pointer" onClick={exportToExcel}>
                            <img className="w-4 h-4" src="images/icon/upload.png" alt="Exportar" />
                            <span className="text-stone-600 text-sm font-medium">Exportar</span>
                        </button>
                    )}
                </div>

                {/* Loading Indicator */}
                {loading && (
                    <div className="flex flex-col items-center justify-center py-12 gap-4">
                        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-stone-600"></div>
                        <p className="text-stone-600 text-lg font-medium">
                            Carregando {pageSize > 100 ? 'muitos' : ''} registros... 
                            {pageSize > 100 && <span className="text-sm block text-slate-500 mt-1">Isso pode levar alguns segundos</span>}
                        </p>
                    </div>
                )}

                {/* Tabela de Dados */}
                {!loading && (
                    <div className="overflow-x-auto">
                        <table className="min-w-full bg-white border-collapse border border-gray-200">
                        <thead className="bg-stone-200">
                            <tr>
                                {columns.filter((col) => col.visible).map((col) => (
                                    <th key={col.id} className="px-4 py-2 border border-slate-200 text-slate-800 text-base font-bold text-left">
                                        {col.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {boletos.length === 0 ? (
                                <tr>
                                    <td colSpan={columns.filter(c => c.visible).length} className="px-4 py-8 text-center text-gray-500">
                                        Nenhum boleto encontrado
                                    </td>
                                </tr>
                            ) : (
                                boletos.map((boleto) => (
                                    <TableRow key={boleto.id} boleto={boleto} columns={columns} />
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                )}

                {/* Paginação */}
                <div className="self-stretch justify-end items-center gap-2.5 inline-flex">
                    <div className="grow shrink basis-0 text-slate-500 text-base font-normal">
                        Mostrando de {totalRecords > 0 ? startRecord : 0} até {endRecord} de {totalRecords} registros.
                    </div>
                    <div className="justify-start items-start gap-4 flex">
                        <button
                            className={`w-10 h-10 bg-white/0 rounded border border-[#bebebe] justify-center items-center flex ${currentPage === 1 || loading ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-300 cursor-pointer"}`}
                            disabled={currentPage === 1 || loading}
                            onClick={() => handlePageChange(currentPage - 1)}
                        >
                            <img className="w-6 h-6" src="images/icon/keyboard-arrow-left.png" alt="Anterior" />
                        </button>
                        {pageNumbers.map((page) => (
                            <button
                                key={page}
                                className={`w-10 h-10 rounded justify-center items-center flex text-base font-semibold ${page === currentPage ? "bg-stone-600 text-white" : "bg-gray-200 text-gray-600"}`}
                                onClick={() => handlePageChange(page)}
                                disabled={loading}
                            >
                                {page}
                            </button>
                        ))}
                        <button
                            className={`w-10 h-10 bg-white/0 rounded border border-[#bebebe] justify-center items-center flex ${currentPage >= totalPages || totalPages === 0 || loading ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'}`}
                            disabled={currentPage >= totalPages || totalPages === 0 || loading}
                            onClick={() => handlePageChange(currentPage + 1)}
                        >
                            <img className="w-6 h-6" src="images/icon/keyboard-arrow-right.png" alt="Próximo" />
                        </button>
                    </div>
                </div>

                {/* Modal de Detalhes */}
                {showDetalheModal && selectedBoleto && (
                    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                        <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                            <div className="p-6">
                                <div className="flex justify-between items-center mb-6">
                                    <h2 className="text-xl font-bold text-gray-800">Detalhes do Boleto</h2>
                                    <button onClick={() => setShowDetalheModal(false)} className="p-2 hover:bg-gray-100 rounded">
                                        <img className="w-6 h-6" src="images/icon/close.png" alt="Fechar" />
                                    </button>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="col-span-2 p-4 bg-gray-50 rounded-lg">
                                        <div className="text-sm text-gray-500 mb-1">Linha Digitável</div>
                                        <div className="flex items-center gap-2">
                                            <code className="flex-1 text-sm bg-white p-2 rounded border break-all">
                                                {selectedBoleto.digitableLine}
                                            </code>
                                            <button
                                                onClick={() => handleCopyCodigoBarras(selectedBoleto.digitableLine)}
                                                className="px-3 py-2 bg-stone-600 text-white rounded hover:bg-stone-700"
                                            >
                                                Copiar
                                            </button>
                                        </div>
                                    </div>

                                    <div>
                                        <div className="text-sm text-gray-500">Valor</div>
                                        <div className="text-lg font-bold text-gray-800">{formatCurrency(selectedBoleto.valor)}</div>
                                    </div>
                                    <div>
                                        <div className="text-sm text-gray-500">Status</div>
                                        <div className="mt-1">{getStatusBadge(selectedBoleto.status)}</div>
                                    </div>

                                    <div>
                                        <div className="text-sm text-gray-500">Data de Emissão</div>
                                        <div className="text-gray-800">{formatDateWithoutHour(selectedBoleto.dataEmissao)}</div>
                                    </div>
                                    <div>
                                        <div className="text-sm text-gray-500">Data de Vencimento</div>
                                        <div className="text-gray-800">{formatDateWithoutHour(selectedBoleto.dataVencimento)}</div>
                                    </div>

                                    <div className="col-span-2 border-t pt-4 mt-2">
                                        <div className="text-sm font-medium text-gray-700 mb-2">Beneficiário</div>
                                        <div className="text-gray-800">{selectedBoleto.beneficiarioNome}</div>
                                        <div className="text-sm text-gray-500">{selectedBoleto.beneficiarioDocumento}</div>
                                        {selectedBoleto.beneficiarioBancoNome && (
                                            <div className="text-sm text-gray-500 mt-1">
                                                Banco: {selectedBoleto.beneficiarioBancoNome} ({selectedBoleto.beneficiarioBanco})
                                            </div>
                                        )}
                                    </div>

                                    <div className="col-span-2 border-t pt-4 mt-2">
                                        <div className="text-sm font-medium text-gray-700 mb-2">Pagador</div>
                                        <div className="text-gray-800">{selectedBoleto.pagadorNome}</div>
                                        <div className="text-sm text-gray-500">{selectedBoleto.pagadorDocumento}</div>
                                        {selectedBoleto.pagadorEndereco && (
                                            <div className="text-sm text-gray-500 mt-1">
                                                {selectedBoleto.pagadorEndereco}, {selectedBoleto.pagadorCidade} - {selectedBoleto.pagadorUf}
                                            </div>
                                        )}
                                    </div>

                                    {(selectedBoleto.notaFiscal || selectedBoleto.notaFiscalServico) && (
                                        <div className="col-span-2 border-t pt-4 mt-2">
                                            <div className="text-sm font-medium text-gray-700 mb-2">Nota Fiscal Vinculada</div>
                                            
                                            {/* NFe de Material */}
                                            {selectedBoleto.notaFiscal && (
                                                <div className="p-3 bg-blue-50 rounded-lg mb-2">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded font-medium">MATERIAL</span>
                                                        <span className="text-gray-800 font-medium">Nota #{selectedBoleto.notaFiscal.numero}</span>
                                                    </div>
                                                    <div className="text-sm text-gray-500">Chave: {selectedBoleto.notaFiscal.chave}</div>
                                                    {selectedBoleto.notaFiscal.fornecedor && (
                                                        <div className="text-sm text-gray-500 mt-1">
                                                            {selectedBoleto.notaFiscal.fornecedor.razaoSocial || selectedBoleto.notaFiscal.fornecedor.nomeFornecedor}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            
                                            {/* NFSe de Serviço */}
                                            {selectedBoleto.notaFiscalServico && (
                                                <div className="p-3 bg-green-50 rounded-lg">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <span className="text-xs bg-green-600 text-white px-2 py-0.5 rounded font-medium">SERVIÇO</span>
                                                        <span className="text-gray-800 font-medium">Nota #{selectedBoleto.notaFiscalServico.numero}</span>
                                                    </div>
                                                    <div className="text-sm text-gray-500">Chave: {selectedBoleto.notaFiscalServico.chave}</div>
                                                    {selectedBoleto.notaFiscalServico.fornecedor && (
                                                        <div className="text-sm text-gray-500 mt-1">
                                                            {selectedBoleto.notaFiscalServico.fornecedor.razaoSocial || selectedBoleto.notaFiscalServico.fornecedor.nomeFornecedor}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {selectedBoleto.observacao && (
                                        <div className="col-span-2 border-t pt-4 mt-2">
                                            <div className="text-sm font-medium text-gray-700 mb-2">Observação</div>
                                            <div className="text-gray-600">{selectedBoleto.observacao}</div>
                                        </div>
                                    )}
                                </div>

                                <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
                                    <button
                                        onClick={() => setShowDetalheModal(false)}
                                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                                    >
                                        Fechar
                                    </button>
                                    <button
                                        onClick={() => {
                                            setShowDetalheModal(false);
                                            handleShowStatusModal(selectedBoleto);
                                        }}
                                        className="px-4 py-2 bg-stone-600 text-white rounded hover:bg-stone-700"
                                    >
                                        Atualizar Status
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal de Atualização de Status */}
                {showStatusModal && selectedBoleto && (
                    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                        <div className="bg-white rounded-lg w-full max-w-md">
                            <div className="p-6">
                                <div className="flex justify-between items-center mb-6">
                                    <h2 className="text-xl font-bold text-gray-800">Atualizar Status</h2>
                                    <button onClick={() => setShowStatusModal(false)} className="p-2 hover:bg-gray-100 rounded">
                                        <img className="w-6 h-6" src="images/icon/close.png" alt="Fechar" />
                                    </button>
                                </div>

                                <div className="flex flex-col gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                                        <select
                                            className="w-full px-3 py-2 bg-white rounded-md border border-slate-300 text-slate-700"
                                            value={statusForm.status}
                                            onChange={(e) => setStatusForm({ ...statusForm, status: e.target.value })}
                                        >
                                            <option value="open">Em Aberto</option>
                                            <option value="paid">Pago</option>
                                            <option value="overdue">Vencido</option>
                                            <option value="closed">Baixado</option>
                                            <option value="canceled">Cancelado</option>
                                        </select>
                                    </div>

                                    {statusForm.status === 'paid' && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Data do Pagamento</label>
                                            <input
                                                type="date"
                                                className="w-full px-3 py-2 bg-white rounded-md border border-slate-300 text-slate-700"
                                                value={statusForm.dataPagamento}
                                                onChange={(e) => setStatusForm({ ...statusForm, dataPagamento: e.target.value })}
                                            />
                                        </div>
                                    )}

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Observação</label>
                                        <textarea
                                            className="w-full px-3 py-2 bg-white rounded-md border border-slate-300 text-slate-700"
                                            rows={3}
                                            value={statusForm.observacao}
                                            onChange={(e) => setStatusForm({ ...statusForm, observacao: e.target.value })}
                                            placeholder="Observação opcional..."
                                        />
                                    </div>
                                </div>

                                <div className="flex justify-end gap-2 mt-6">
                                    <button
                                        onClick={() => setShowStatusModal(false)}
                                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        onClick={handleAtualizarStatus}
                                        className="px-4 py-2 bg-stone-600 text-white rounded hover:bg-stone-700"
                                    >
                                        Salvar
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal de PDF */}
                {showPdfModal && (
                    <div className="fixed inset-0 bg-black/90 p-10 flex-col justify-start items-end gap-10 inline-flex overflow-hidden" style={{ zIndex: 60 }}>
                        <div className="rounded-lg w-full max-w-[80%] h-full max-h-[90%] flex flex-col">
                            <div className="flex justify-between items-center p-4">
                                <div className="flex items-center gap-4">
                                    <a href={selectedPdfUrl} download target="_blank" rel="noreferrer">
                                        <img className="w-6 h-6" src="images/icon/download.png" alt="Download" />
                                    </a>
                                </div>
                                <img
                                    className="w-6 h-6 cursor-pointer"
                                    src="images/icon/x.png"
                                    onClick={() => setShowPdfModal(false)}
                                    alt="Fechar"
                                />
                            </div>
                            <div className="flex-grow flex justify-center items-center p-4 overflow-hidden">
                                <iframe
                                    src={`${selectedPdfUrl}#toolbar=0`}
                                    className="w-full h-full border-0"
                                    title="PDF Viewer"
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal de Vinculação de Nota Fiscal */}
                {showVincularModal && selectedBoleto && (
                    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                        <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
                            <div className="p-6">
                                <div className="flex justify-between items-center mb-6">
                                    <h2 className="text-xl font-bold text-gray-800">Vincular Nota Fiscal ao Boleto</h2>
                                    <button onClick={() => setShowVincularModal(false)} className="p-2 hover:bg-gray-100 rounded">
                                        <img className="w-6 h-6" src="images/icon/close.png" alt="Fechar" />
                                    </button>
                                </div>

                                {/* Informações do Boleto */}
                                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                                    <div className="text-sm font-medium text-gray-700 mb-2">Boleto Selecionado</div>
                                    <div className="grid grid-cols-2 gap-2 text-sm">
                                        <div>
                                            <span className="text-gray-500">Beneficiário:</span>
                                            <span className="ml-2 text-gray-800">{selectedBoleto.beneficiarioNome}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-500">Valor:</span>
                                            <span className="ml-2 text-gray-800 font-medium">{formatCurrency(selectedBoleto.valor)}</span>
                                        </div>
                                        <div className="col-span-2">
                                            <span className="text-gray-500">CNPJ:</span>
                                            <span className="ml-2 text-gray-800">{selectedBoleto.beneficiarioDocumento}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Nota Atualmente Vinculada (se houver) */}
                                {(selectedBoleto.notaFiscal || selectedBoleto.notaFiscalServico) && (
                                    <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="text-sm font-medium text-yellow-800">Nota Atualmente Vinculada</div>
                                            <button
                                                onClick={handleDesvincularNota}
                                                className="px-3 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600"
                                            >
                                                Desvincular
                                            </button>
                                        </div>
                                        
                                        {selectedBoleto.notaFiscal && (
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded font-medium">MATERIAL</span>
                                                <span className="text-sm text-gray-700">Nota #{selectedBoleto.notaFiscal.numero}</span>
                                            </div>
                                        )}
                                        
                                        {selectedBoleto.notaFiscalServico && (
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs bg-green-600 text-white px-2 py-0.5 rounded font-medium">SERVIÇO</span>
                                                <span className="text-sm text-gray-700">Nota #{selectedBoleto.notaFiscalServico.numero}</span>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Filtros de Busca */}
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Buscar Nota Fiscal</label>
                                    
                                    {/* Tipo de Nota */}
                                    <div className="flex gap-2 mb-3">
                                        <button
                                            onClick={() => handleChangeTipoNota('todos')}
                                            className={`px-4 py-2 rounded text-sm font-medium ${
                                                tipoNotaVinculo === 'todos'
                                                    ? 'bg-stone-600 text-white'
                                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                            }`}
                                        >
                                            Todos
                                        </button>
                                        <button
                                            onClick={() => handleChangeTipoNota('material')}
                                            className={`px-4 py-2 rounded text-sm font-medium ${
                                                tipoNotaVinculo === 'material'
                                                    ? 'bg-blue-600 text-white'
                                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                            }`}
                                        >
                                            Material
                                        </button>
                                        <button
                                            onClick={() => handleChangeTipoNota('servico')}
                                            className={`px-4 py-2 rounded text-sm font-medium ${
                                                tipoNotaVinculo === 'servico'
                                                    ? 'bg-green-600 text-white'
                                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                            }`}
                                        >
                                            Serviço
                                        </button>
                                    </div>

                                    {/* Campo de Busca */}
                                    <input
                                        type="text"
                                        className="w-full px-3 py-2 bg-white rounded-md border border-slate-300 text-slate-700"
                                        placeholder="Digite o número da nota, chave ou fornecedor..."
                                        value={searchNotaFiscal}
                                        onChange={handleSearchNotaFiscal}
                                    />
                                </div>

                                {/* Lista de Notas Fiscais */}
                                <div className="mb-4">
                                    <div className="text-sm font-medium text-gray-700 mb-2">Selecione uma Nota Fiscal</div>
                                    
                                    {buscandoNotas ? (
                                        <div className="text-center py-8 text-gray-500">
                                            Carregando notas fiscais...
                                        </div>
                                    ) : notasFiscaisDisponiveis.length === 0 ? (
                                        <div className="text-center py-8 text-gray-500">
                                            Nenhuma nota fiscal encontrada para este beneficiário.
                                        </div>
                                    ) : (
                                        <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
                                            {notasFiscaisDisponiveis.map((nota) => (
                                                <div
                                                    key={`${nota.tipo}-${nota.id}`}
                                                    onClick={() => setNotaSelecionadaVinculo(nota)}
                                                    className={`p-3 border-b border-gray-200 cursor-pointer hover:bg-gray-50 ${
                                                        notaSelecionadaVinculo?.id === nota.id && notaSelecionadaVinculo?.tipo === nota.tipo
                                                            ? 'bg-blue-50 border-l-4 border-l-blue-600'
                                                            : ''
                                                    }`}
                                                >
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex-1">
                                                            <div className="flex items-center gap-2 mb-1">
                                                                <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                                                                    nota.tipo === 'material'
                                                                        ? 'bg-blue-600 text-white'
                                                                        : 'bg-green-600 text-white'
                                                                }`}>
                                                                    {nota.tipoLabel}
                                                                </span>
                                                                <span className="text-sm font-medium text-gray-800">
                                                                    Nota #{nota.numero}
                                                                </span>
                                                            </div>
                                                            <div className="text-xs text-gray-500 truncate">
                                                                Chave: {nota.chave}
                                                            </div>
                                                            {nota.fornecedor && (
                                                                <div className="text-xs text-gray-600 mt-1">
                                                                    {nota.fornecedor.razaoSocial || nota.fornecedor.nomeFornecedor}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="text-sm font-medium text-gray-700">
                                                            {formatCurrency(nota.valorFormatado)}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Botões de Ação */}
                                <div className="flex justify-end gap-2 pt-4 border-t">
                                    <button
                                        onClick={() => setShowVincularModal(false)}
                                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        onClick={handleVincularNota}
                                        disabled={!notaSelecionadaVinculo}
                                        className={`px-4 py-2 rounded ${
                                            notaSelecionadaVinculo
                                                ? 'bg-stone-600 text-white hover:bg-stone-700'
                                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                        }`}
                                    >
                                        Vincular Nota Selecionada
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal de Anexos/Documentos */}
                {showAnexosModal && selectedBoleto && (
                    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                        <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
                            <div className="p-6">
                                <div className="flex justify-between items-center mb-6">
                                    <h2 className="text-xl font-bold text-gray-800">Documentos do Boleto</h2>
                                    <button onClick={() => setShowAnexosModal(false)} className="p-2 hover:bg-gray-100 rounded">
                                        <img className="w-6 h-6" src="images/icon/close.png" alt="Fechar" />
                                    </button>
                                </div>

                                {/* Informações do Boleto */}
                                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                                    <div className="text-sm font-medium text-gray-700 mb-2">Boleto Selecionado</div>
                                    <div className="grid grid-cols-2 gap-2 text-sm">
                                        <div>
                                            <span className="text-gray-500">Beneficiário:</span>
                                            <span className="ml-2 text-gray-800">{selectedBoleto.beneficiarioNome}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-500">Valor:</span>
                                            <span className="ml-2 text-gray-800 font-medium">{formatCurrency(selectedBoleto.valor)}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-500">Vencimento:</span>
                                            <span className="ml-2 text-gray-800">{selectedBoleto.dataVencimento ? formatDateWithoutHour(selectedBoleto.dataVencimento) : '-'}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Formulário de Upload */}
                                {podeAtualizar && (
                                    <div className="mb-6">
                                        <div className="text-sm font-medium text-gray-700 mb-3">Anexar Novo Documento</div>
                                        <div className="flex flex-col md:flex-row gap-3">
                                            <div className="w-full md:w-[200px]">
                                                <select
                                                    className="w-full px-3 py-2 bg-white rounded-md border border-slate-300 text-slate-700 text-sm"
                                                    value={anexoTipoId}
                                                    onChange={(e) => setAnexoTipoId(e.target.value)}
                                                >
                                                    <option value="">Tipo de Documento</option>
                                                    {tiposDocumentos.map((tipo) => (
                                                        <option key={tipo.id} value={tipo.id}>{tipo.nome}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="flex-1">
                                                <div className="relative flex items-center w-full">
                                                    <input
                                                        type="file"
                                                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                                                        onChange={(e) => setAnexoFile(e.target.files[0])}
                                                        className="absolute inset-0 opacity-0 cursor-pointer"
                                                    />
                                                    <div className="w-full px-3 py-2 bg-white rounded-md border border-slate-300 flex items-center">
                                                        <span className="text-sm text-slate-400 truncate">
                                                            {anexoFile ? anexoFile.name : "Selecionar arquivo..."}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                            <button
                                                onClick={handleAddAnexo}
                                                disabled={enviandoAnexo}
                                                className={`px-4 py-2 rounded-md text-white text-sm font-medium whitespace-nowrap ${
                                                    enviandoAnexo 
                                                        ? 'bg-gray-400 cursor-not-allowed' 
                                                        : 'bg-stone-600 hover:bg-stone-700'
                                                }`}
                                            >
                                                {enviandoAnexo ? 'Enviando...' : 'Enviar'}
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Lista de Documentos */}
                                <div>
                                    <div className="text-sm font-medium text-gray-700 mb-3">
                                        Documentos Anexados ({anexosBoleto.length})
                                    </div>

                                    {loadingAnexos ? (
                                        <div className="text-center py-8 text-gray-500">
                                            Carregando documentos...
                                        </div>
                                    ) : anexosBoleto.length === 0 ? (
                                        <div className="text-center py-8 text-gray-400 border border-dashed border-gray-300 rounded-lg">
                                            Nenhum documento anexado a este boleto.
                                        </div>
                                    ) : (
                                        <div className="overflow-x-auto">
                                            <table className="border-collapse w-full min-w-max">
                                                <thead>
                                                    <tr>
                                                        <th className="px-4 py-2 bg-stone-200 border border-slate-200 text-slate-800 text-sm font-bold text-left">Tipo</th>
                                                        <th className="px-4 py-2 bg-stone-200 border border-slate-200 text-slate-800 text-sm font-bold text-left">Arquivo</th>
                                                        <th className="px-4 py-2 bg-stone-200 border border-slate-200 text-slate-800 text-sm font-bold text-left">Data Envio</th>
                                                        <th className="px-4 py-2 bg-stone-200 border border-slate-200 text-slate-800 text-sm font-bold text-left">Enviado Por</th>
                                                        <th className="px-4 py-2 bg-stone-200 border border-slate-200 text-slate-800 text-sm font-bold text-center">Ações</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {anexosBoleto.map((anexo) => (
                                                        <tr key={anexo.id}>
                                                            <td className="px-4 py-2 bg-white border border-slate-200 text-slate-700 text-sm">
                                                                <span className="px-2 py-1 bg-stone-100 rounded text-xs font-medium">
                                                                    {anexo.tipo?.nome || '-'}
                                                                </span>
                                                            </td>
                                                            <td className="px-4 py-2 bg-white border border-slate-200 text-slate-700 text-sm truncate max-w-[200px]">
                                                                {anexo.arquivo?.split('/').pop() || '-'}
                                                            </td>
                                                            <td className="px-4 py-2 bg-white border border-slate-200 text-slate-700 text-sm">
                                                                {formatDateWithoutHour(anexo.dataEnvio)}
                                                            </td>
                                                            <td className="px-4 py-2 bg-white border border-slate-200 text-slate-700 text-sm">
                                                                {anexo.enviadoPor?.nome || '-'}
                                                            </td>
                                                            <td className="px-4 py-2 bg-white border border-slate-200 text-center">
                                                                <div className="flex justify-center gap-2">
                                                                    {anexo.arquivo && (
                                                                        <button
                                                                            onClick={() => handleViewAnexo(anexo.arquivo)}
                                                                            className="p-1 hover:bg-stone-100 rounded"
                                                                            title="Visualizar"
                                                                        >
                                                                            <img className="w-5 h-5" src="images/icon/search-file.png" alt="Ver" />
                                                                        </button>
                                                                    )}
                                                                    {podeAtualizar && (
                                                                        <button
                                                                            onClick={() => handleRemoveAnexo(anexo.id)}
                                                                            className="p-1 hover:bg-red-100 rounded"
                                                                            title="Remover"
                                                                        >
                                                                            <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                            </svg>
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>

                                {/* Botão Fechar */}
                                <div className="flex justify-end mt-6 pt-4 border-t">
                                    <button
                                        onClick={() => setShowAnexosModal(false)}
                                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                                    >
                                        Fechar
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <AlertDialog
                    isVisible={showSuccessDialog}
                    message={message}
                    onClose={() => setShowSuccessDialog(false)}
                />
            </div>
        </Template>
    );
};

export default Boletos;
